<template>
  <div
    class="min-h-[calc(100vh-4rem)] overflow-x-hidden bg-base-200/50 dark:bg-gray-950"
    :dir="isRTL === true ? 'rtl' : 'ltr'"
  >
    <div class="mx-auto max-w-6xl px-4 py-6 md:px-6 md:pb-10">
      <header class="mb-6 flex flex-col sm:mb-7 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-start" :class="isRTL === true ? 'flex-row-reverse' : ''">
          <span
            class="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-base-200/90 text-base-content ring-1 ring-base-content/5 dark:bg-gray-800 dark:ring-white/10"
            :class="isRTL === true ? 'ml-4' : 'mr-4'"
            aria-hidden="true"
          >
            <Icon icon="lucide:sparkles" class="h-6 w-6 opacity-90" />
          </span>
          <div class="min-w-0">
            <h1
              class="text-2xl font-bold tracking-tight text-base-content"
              :style="isRTL === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }"
            >
              AutoAgent
            </h1>
            <p
              class="mt-1 max-w-xl text-sm text-base-content/65"
              :style="isRTL === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }"
            >
              Create an AI agent automatically from a website
            </p>
          </div>
        </div>
        <div v-if="currentStep !== 1" class="mt-4 shrink-0 sm:mt-0">
          <button
            type="button"
            @click="resetAll"
            class="inline-flex h-11 items-center justify-center rounded-2xl border border-base-300/80 bg-base-100 px-4 text-sm font-semibold text-base-content shadow-sm transition-all duration-200 hover:border-base-content/15 hover:bg-base-200/50 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-base-content/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700/90 dark:bg-gray-900/35 dark:hover:border-gray-600 dark:hover:bg-gray-800/50"
            :disabled="isStreaming === true || isConfirming === true"
          >
            <Icon icon="lucide:rotate-ccw" class="h-4 w-4 opacity-80" :class="isRTL === true ? 'ml-3' : 'mr-3'" />
            Reset
          </button>
        </div>
      </header>

      <section
        class="mb-6 rounded-3xl border border-base-300/80 bg-base-100 p-4 shadow-sm ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-950 dark:ring-white/10 sm:p-5"
        aria-label="Progress"
      >
        <div class="flex w-full items-start justify-between">
          <template v-for="s in [1, 2, 3, 4]" :key="'step-node-' + s">
            <div class="flex w-[4.5rem] shrink-0 flex-col items-center sm:w-[5.5rem]">
              <div
                class="flex h-9 w-9 items-center justify-center rounded-full border-2 text-xs font-bold transition-all duration-200"
                :class="
                  currentStep >= s
                    ? 'border-base-content/25 bg-base-content text-base-100 shadow-sm ring-2 ring-base-content/15 dark:ring-base-content/20'
                    : 'border-base-300/80 bg-base-200/70 text-base-content/45 dark:border-gray-700 dark:bg-gray-800/80'
                "
              >
                {{ s }}
              </div>
              <span
                class="mt-2 text-center text-[0.65rem] font-semibold uppercase leading-tight tracking-wide sm:text-[0.7rem]"
                :class="currentStep >= s ? 'text-base-content' : 'text-base-content/50'"
              >
                <template v-if="s === 1">Configuration</template>
                <template v-else-if="s === 2">Analysis</template>
                <template v-else-if="s === 3">Review</template>
                <template v-else>Deployment</template>
              </span>
            </div>
            <div
              v-if="s < 4"
              class="mx-1 mt-[1.125rem] h-0.5 min-w-[0.5rem] flex-1 rounded-full transition-colors duration-300"
              :class="currentStep > s ? 'bg-base-content/40 dark:bg-base-content/35' : 'bg-base-300/80 dark:bg-gray-700'"
              aria-hidden="true"
            />
          </template>
        </div>
      </section>

      <div v-if="currentStep === 1" class="mb-4">
        <div
          class="rounded-3xl border border-base-300/80 bg-base-100 p-5 shadow-xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-950 dark:ring-white/10 sm:p-6 md:p-8"
        >
          <div class="mb-6 border-b border-base-300/80 pb-5 dark:border-gray-800">
            <h2 class="text-lg font-bold tracking-tight text-base-content">Agent configuration</h2>
            <p class="mt-1 text-sm text-base-content/65">
              Point us at your site. We extract brand, tone, and structure to build your agent.
            </p>
          </div>
          <div class="flex flex-col">
            <div class="form-control">
              <label class="label pb-1 pt-0">
                <span class="label-text font-medium text-base-content">Target website URL <span class="text-error">*</span></span>
              </label>
              <label
                class="input input-bordered flex h-11 items-center rounded-xl border-base-300/80 bg-base-100 transition-shadow focus-within:border-base-content/20 focus-within:outline-none focus-within:ring-2 focus-within:ring-base-content/10 dark:border-gray-700 dark:bg-gray-950"
              >
                <Icon icon="lucide:globe" class="h-4 w-4 shrink-0 text-base-content/45" :class="isRTL === true ? 'ml-3' : 'mr-3'" />
                <input
                  type="text"
                  v-model="form.websiteUrl"
                  class="min-w-0 flex-1 border-0 bg-transparent text-sm text-base-content outline-none focus:ring-0"
                  placeholder="https://example.com"
                  @keyup.enter="startAnalysis"
                />
              </label>
            </div>

            <div class="mt-5 flex flex-col md:mt-5 md:flex-row md:items-start">
              <div class="form-control min-w-0 flex-1">
                <label class="label pb-1 pt-0">
                  <span class="label-text font-medium text-base-content">Agent name</span>
                </label>
                <input
                  type="text"
                  v-model="form.agentName"
                  class="input input-bordered h-11 w-full rounded-xl border-base-300/80 bg-base-100 text-base-content transition-shadow focus:border-base-content/20 focus:outline-none focus:ring-2 focus:ring-base-content/10 dark:border-gray-700 dark:bg-gray-950"
                  placeholder="e.g. Customer support agent"
                />
              </div>
              <div
                class="form-control mt-5 min-w-0 flex-1 md:mt-0"
                :class="isRTL === true ? 'md:mr-6' : 'md:ml-6'"
              >
                <label class="label pb-1 pt-0">
                  <span class="label-text font-medium text-base-content">Industry category</span>
                </label>
                <input
                  type="text"
                  v-model="form.industry"
                  class="input input-bordered h-11 w-full rounded-xl border-base-300/80 bg-base-100 text-base-content transition-shadow focus:border-base-content/20 focus:outline-none focus:ring-2 focus:ring-base-content/10 dark:border-gray-700 dark:bg-gray-950"
                  placeholder="e.g. SaaS, E-commerce"
                />
              </div>
            </div>

            <div class="form-control mt-5">
              <label class="label pb-1 pt-0">
                <span class="label-text font-medium text-base-content">Additional context</span>
              </label>
              <textarea
                v-model="form.agentDescription"
                class="textarea textarea-bordered min-h-[5.5rem] w-full rounded-xl border-base-300/80 bg-base-100 text-base-content transition-shadow focus:border-base-content/20 focus:outline-none focus:ring-2 focus:ring-base-content/10 dark:border-gray-700 dark:bg-gray-950"
                rows="3"
                placeholder="Specific instructions or focus areas for this agent…"
              ></textarea>
            </div>

            <div class="form-control mt-6">
              <button
                type="button"
                @click="startAnalysis"
                class="inline-flex w-full items-center justify-center rounded-xl border border-neutral-900 bg-neutral-900 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-all duration-150 hover:bg-neutral-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 dark:border-neutral-700 dark:bg-neutral-800 dark:text-white dark:hover:bg-neutral-700"
                :disabled="form.websiteUrl.trim().length === 0"
              >
                Start analysis
                <Icon icon="lucide:arrow-right" class="h-4 w-4" :class="isRTL === true ? 'mr-3' : 'ml-3'" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="currentStep === 2" class="mb-4">
        <div
          class="rounded-3xl border border-base-300/80 bg-base-100 px-5 py-10 text-center shadow-xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-950 dark:ring-white/10 sm:px-8 sm:py-12"
        >
          <div class="relative mx-auto flex h-20 w-20 items-center justify-center">
            <span
              class="pointer-events-none absolute inset-0 animate-pulse rounded-full border-2 border-base-content/15 opacity-40 dark:border-base-content/25"
            ></span>
            <span class="loading loading-spinner loading-lg relative z-10 text-base-content opacity-80"></span>
          </div>
          <h2 class="mt-6 text-lg font-bold tracking-tight text-base-content">Analyzing brand DNA</h2>
          <p class="mx-auto mt-2 max-w-lg text-sm text-base-content/65">
            Synchronizing with your URL and extracting semantic patterns from the live site.
          </p>
          <div class="mx-auto mt-8 w-full max-w-xl text-left">
            <div v-for="(event, idx) in progressEvents" :key="idx" class="mt-3 flex items-start first:mt-0">
              <Icon
                icon="lucide:check-circle-2"
                class="mt-0.5 h-4 w-4 shrink-0 text-success"
                :class="isRTL === true ? 'ml-2' : 'mr-2'"
              />
              <span class="text-sm leading-snug text-base-content/80">{{ event.message }}</span>
            </div>
            <div v-if="isStreaming === true" class="mt-4 flex items-center">
              <span class="loading loading-spinner loading-xs text-base-content" :class="isRTL === true ? 'ml-2' : 'mr-2'"></span>
              <span class="text-sm font-semibold text-base-content">Processing patterns…</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="currentStep === 3 && preview !== null" class="mb-4">
        <div class="flex flex-col lg:flex-row lg:items-start">
          <div class="w-full min-w-0 lg:w-[58%]">
            <div
              class="overflow-hidden rounded-3xl border border-base-300/80 bg-base-100 shadow-xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-950 dark:ring-white/10"
            >
              <div
                class="h-1.5 w-full"
                :style="{ background: `linear-gradient(90deg, ${preview.theme.primaryColor}, ${preview.theme.secondaryColor}, ${preview.theme.accentColor})` }"
              />
              <div class="p-5 sm:p-6 md:p-8">
                <div class="flex items-start" :class="isRTL === true ? 'flex-row-reverse' : ''">
                  <div class="avatar shrink-0" :class="isRTL === true ? 'ml-5' : 'mr-5'">
                    <div
                      class="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl bg-base-200/90 ring-1 ring-base-content/5 dark:bg-gray-800 dark:ring-white/10"
                    >
                      <img v-if="preview.iconDataUrl !== ''" :src="preview.iconDataUrl" class="h-full w-full object-cover" alt="" />
                      <Icon v-else icon="lucide:bot" class="h-8 w-8 text-base-content/35" />
                    </div>
                  </div>
                  <div class="min-w-0 flex-1">
                    <input
                      v-model="editable.agentName"
                      class="w-full border-0 bg-transparent p-0 text-xl font-semibold tracking-tight text-base-content placeholder:text-base-content/40 focus:outline-none focus:ring-0"
                    />
                    <textarea
                      v-model="editable.agentDescription"
                      class="mt-2 w-full resize-none overflow-hidden border-0 bg-transparent p-0 text-sm leading-relaxed text-base-content/70 focus:outline-none focus:ring-0"
                      rows="2"
                    ></textarea>
                  </div>
                </div>

                <div class="mt-8">
                  <div class="form-control">
                    <label class="label pb-1 pt-0">
                      <span class="label-text font-medium text-base-content">Core intelligence</span>
                    </label>
                    <textarea
                      v-model="editable.adminDescription"
                      class="textarea textarea-bordered min-h-[6rem] w-full rounded-xl border-base-300/80 bg-base-100 text-base-content focus:border-base-content/20 focus:outline-none focus:ring-2 focus:ring-base-content/10 dark:border-gray-700 dark:bg-gray-950"
                      rows="4"
                    ></textarea>
                  </div>

                  <div class="form-control mt-6">
                    <label class="label pb-1 pt-0">
                      <span class="label-text font-medium text-base-content">Extracted brand palette</span>
                    </label>
                    <div class="flex flex-wrap">
                      <div
                        v-for="(color, name) in preview.theme"
                        :key="name"
                        class="mb-2 mr-2 inline-flex items-center rounded-md border border-base-300/60 bg-base-100/80 py-1.5 pl-2 pr-3 dark:border-gray-600 dark:bg-gray-900/50"
                      >
                        <div
                          class="h-4 w-4 shrink-0 rounded-md ring-1 ring-black/5 dark:ring-white/10"
                          :style="{ backgroundColor: color }"
                        />
                        <span class="font-mono text-[11px] font-medium text-base-content/80" :class="isRTL === true ? 'mr-2' : 'ml-2'">{{
                          name.replace('Color', '')
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="mt-6 w-full min-w-0 lg:mt-0 lg:w-[42%]"
            :class="isRTL === true ? 'lg:pr-6' : 'lg:pl-6'"
          >
            <div
              class="rounded-3xl border border-base-300/80 bg-base-100 p-5 shadow-xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-950 dark:ring-white/10 sm:p-6"
            >
              <h3 class="m-0 flex items-center text-base font-bold tracking-tight text-base-content">
                <span
                  class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-base-200/90 ring-1 ring-base-content/5 dark:bg-gray-800 dark:ring-white/5"
                  :class="isRTL === true ? 'ml-2.5' : 'mr-2.5'"
                >
                  <Icon icon="lucide:layers" class="h-5 w-5 text-base-content/80" />
                </span>
                Knowledge acquisition plan
              </h3>
              <ul class="m-0 mt-4 list-none p-0">
                <li v-for="(line, i) in preview.knowledgeBasePlan" :key="i" class="mt-3 flex items-start first:mt-0">
                  <span
                    class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-base-content/50"
                    :class="isRTL === true ? 'ml-2' : 'mr-2'"
                    aria-hidden="true"
                  />
                  <span class="text-sm leading-relaxed text-base-content/70">{{ line }}</span>
                </li>
              </ul>
              <div class="my-6 h-px bg-base-300/80 dark:bg-gray-800" />
              <div class="flex flex-col">
                <button
                  type="button"
                  @click="confirmMaterialization"
                  class="inline-flex w-full items-center justify-center rounded-xl border border-neutral-900 bg-neutral-900 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-all duration-150 hover:bg-neutral-800 active:scale-[0.98] dark:border-neutral-700 dark:bg-neutral-800 dark:text-white dark:hover:bg-neutral-700"
                >
                  Confirm &amp; deploy
                  <Icon icon="lucide:rocket" class="h-4 w-4" :class="isRTL === true ? 'mr-3' : 'ml-3'" />
                </button>
                <button
                  type="button"
                  @click="currentStep = 1"
                  class="btn btn-ghost mt-3 w-full rounded-xl font-medium normal-case text-base-content/80 hover:bg-base-200/80"
                >
                  Back to configuration
                </button>
              </div>
            </div>

            <div
              class="mt-4 flex items-start rounded-2xl border border-base-300/80 bg-base-200/30 p-4 dark:border-gray-800 dark:bg-gray-900/50"
            >
              <Icon
                icon="lucide:info"
                class="mt-0.5 h-5 w-5 shrink-0 text-base-content/55"
                :class="isRTL === true ? 'ml-3' : 'mr-3'"
              />
              <p class="m-0 text-sm leading-relaxed text-base-content/65">
                Confirming runs a deep crawl and indexing. You can finish setup in the agent editor right after creation.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="currentStep === 4" class="mb-4">
        <div
          class="rounded-3xl border border-base-300/80 bg-base-100 px-5 py-10 text-center shadow-xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-950 dark:ring-white/10 sm:px-8 sm:py-12"
        >
          <div class="mx-auto flex h-20 w-20 items-center justify-center">
            <span class="loading loading-spinner loading-lg text-base-content opacity-80"></span>
          </div>
          <h2 class="mt-6 text-lg font-bold tracking-tight text-base-content">Deploying agent</h2>
          <p class="mx-auto mt-2 max-w-md text-sm text-base-content/65">Deep crawling target pages and populating vector stores.</p>
          <div class="mx-auto mt-8 h-2 w-full max-w-md overflow-hidden rounded-full bg-base-300/80 dark:bg-gray-800">
            <div class="h-full rounded-full bg-base-content/70 transition-all duration-300 dark:bg-base-content/60" style="width: 40%"></div>
          </div>
          <div
            class="mx-auto mt-8 w-full max-w-md rounded-2xl border border-base-300/80 bg-base-200/40 p-4 dark:border-gray-800 dark:bg-gray-900/80"
          >
            <div class="flex items-center justify-between">
              <div :class="isRTL === true ? 'text-right' : 'text-left'">
                <p class="mb-1 text-xs font-semibold uppercase tracking-wide text-base-content/50">Instance ID</p>
                <p class="break-all font-mono text-sm font-medium text-base-content">
                  {{ createdAgentId !== null && createdAgentId !== '' ? createdAgentId : 'Generating…' }}
                </p>
              </div>
              <Icon icon="lucide:hash" class="h-5 w-5 shrink-0 text-base-content/40" :class="isRTL === true ? 'mr-0 ml-2' : 'ml-2'" />
            </div>
          </div>
          <div class="mt-8 flex items-center justify-center text-xs text-base-content/55">
            <span class="loading loading-spinner loading-xs text-base-content" :class="isRTL === true ? 'ml-2' : 'mr-2'"></span>
            <span>Deploying to production cluster</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { globalState } from '@/globalState';
import { useToast } from 'vue-toastification';
import { useI18n } from '@/i18n';
import axiosInstance from '@/api/axiosInstance';

const router = useRouter();
const toast = useToast();
const { currentLanguage, availableLanguages } = useI18n();

const currentStep = ref(1);
const isStreaming = ref(false);
const isConfirming = ref(false);
const progressEvents = ref([]);
const preview = ref(null);
const createdAgentId = ref(null);

const form = reactive({
  websiteUrl: '',
  agentName: '',
  agentDescription: '',
  industry: ''
});

const editable = reactive({
  agentName: '',
  agentDescription: '',
  adminDescription: '',
  knowledgeBasePlanText: ''
});

let eventSource = null;

const isRTL = computed(() => {
  const lang = availableLanguages.value.find(l => l.code === currentLanguage.value);
  if (lang !== undefined && lang !== null && lang.dir === 'rtl') {
    return true;
  }
  return false;
});

const resetAll = () => {
  if (eventSource !== null) {
    eventSource.close();
    eventSource = null;
  }
  currentStep.value = 1;
  isStreaming.value = false;
  isConfirming.value = false;
  progressEvents.value = [];
  preview.value = null;
  createdAgentId.value = null;
  form.websiteUrl = '';
  form.agentName = '';
  form.agentDescription = '';
  form.industry = '';
};

const startAnalysis = () => {
  if (typeof form.websiteUrl !== 'string' || form.websiteUrl.trim().length === 0) {
    toast.error('Enter a valid website URL.');
    return;
  }
  
  currentStep.value = 2;
  isStreaming.value = true;
  progressEvents.value = [];
  preview.value = null;

  const url = `${globalState.apiEndpoint}/api/autoAgent/preview/stream?websiteUrl=${encodeURIComponent(form.websiteUrl)}&agentName=${encodeURIComponent(form.agentName)}&agentDescription=${encodeURIComponent(form.agentDescription)}`;

  eventSource = new EventSource(url, { withCredentials: true });

  eventSource.addEventListener('progress', (e) => {
    try {
      const data = JSON.parse(e.data);
      if (data !== undefined && data !== null) {
        progressEvents.value.push(data);
      }
    } catch (err) {}
  });

  eventSource.addEventListener('complete', (e) => {
    try {
      const data = JSON.parse(e.data);
      if (data !== undefined && data !== null) {
        preview.value = data;
        editable.agentName = typeof data.agentName === 'string' ? data.agentName : '';
        editable.agentDescription = typeof data.agentDescription === 'string' ? data.agentDescription : '';
        editable.adminDescription = typeof data.adminDescription === 'string' ? data.adminDescription : '';
        editable.knowledgeBasePlanText = Array.isArray(data.knowledgeBasePlan) === true ? data.knowledgeBasePlan.join('\n') : '';
        isStreaming.value = false;
        if (eventSource !== null) {
          eventSource.close();
          eventSource = null;
        }
        currentStep.value = 3;
      }
    } catch (err) {
      isStreaming.value = false;
    }
  });

  eventSource.addEventListener('error', (e) => {
    isStreaming.value = false;
    if (eventSource !== null) {
      eventSource.close();
      eventSource = null;
    }
    toast.error('Neural analysis failed. Please check the URL.');
    currentStep.value = 1;
  });
};

const confirmMaterialization = async () => {
  isConfirming.value = true;
  currentStep.value = 4;

  try {
    const kbPlan = typeof editable.knowledgeBasePlanText === 'string' ? editable.knowledgeBasePlanText.split('\n') : [];
    const payload = {
      websiteUrl: form.websiteUrl,
      agentName: editable.agentName,
      agentDescription: editable.agentDescription,
      adminDescription: editable.adminDescription,
      theme: preview.value !== null ? preview.value.theme : {},
      iconDataUrl: preview.value !== null ? preview.value.iconDataUrl : '',
      knowledgeBasePlan: kbPlan.filter(l => l.trim().length > 0),
      maxPages: 100
    };

    const baseUrl = typeof globalState.apiEndpoint === 'string' ? globalState.apiEndpoint.trim() : '';
    const endpoint = baseUrl.length > 0 ? `${baseUrl}/api/autoAgent/confirm` : '/api/autoAgent/confirm';
    const response = await axiosInstance.post(endpoint, payload);
    const result = response.data;
    if (result !== undefined && result !== null && typeof result.agentId === 'string') {
      createdAgentId.value = result.agentId;
      toast.success('Agent materialized successfully.');
      
      setTimeout(() => {
        router.push({
          path: '/aiChatAgentSetup',
          query: { edit: result.agentId }
        });
      }, 3000);
    }
  } catch (error) {
    if (error.response !== undefined && error.response !== null && error.response.status === 403) {
      toast.error('Authentication failed. Please refresh the page and try again.');
    } else {
      toast.error('Failed to materialize agent.');
    }
    currentStep.value = 3;
    isConfirming.value = false;
  }
};

onUnmounted(() => {
  if (eventSource !== null) {
    eventSource.close();
  }
});
</script>
